// Small math shared by the rig: the yaw convention, wrap-aware angles, frame-rate independent damping.
//
// The yaw convention (open question 3 in the engine suggestions): yaw 0 faces +Z, as the kernel's `Transform` does,
// and yaw grows counter-clockwise seen from above (towards +X). `forwardOf(yaw)` is (sin yaw, 0, cos yaw) and
// `rightOf(yaw)` is forward × up, (-cos yaw, 0, sin yaw). A controller turns a stick (x right, y forward) into a world
// direction with the same two functions, so the camera's yaw and the hero's agree.

/** A point or direction in world space (metres). */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** The horizontal unit vector a yaw faces. */
export function forwardOf(yaw: number): Vec3 {
  return { x: Math.sin(yaw), y: 0, z: Math.cos(yaw) };
}

/** The horizontal unit vector to the right of a yaw (screen right for a camera with that yaw). */
export function rightOf(yaw: number): Vec3 {
  return { x: -Math.cos(yaw), y: 0, z: Math.sin(yaw) };
}

/** The yaw of a horizontal direction (inverse of `forwardOf`); 0 for a zero vector. */
export function yawOf(x: number, z: number): number {
  return x === 0 && z === 0 ? 0 : Math.atan2(x, z);
}

/** Wrap an angle to (-π, π]. */
export function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

/** The share of the gap an exponential follow at `rate` (1/s) closes in `dt` seconds: 1 - e^(-rate·dt). */
export function damp(rate: number, dt: number): number {
  return rate <= 0 || dt <= 0 ? 0 : 1 - Math.exp(-rate * dt);
}

/** The unit direction from the pivot to the camera for an orbit yaw and pitch (pitch > 0 puts the camera above). */
export function orbitDirection(yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  return { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };
}

/** Smoothstep easing on [0, 1]. */
export function smoothstep(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
export const add = (a: Vec3, b: Vec3, s = 1): Vec3 => ({ x: a.x + b.x * s, y: a.y + b.y * s, z: a.z + b.z * s });
export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
export const length = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);
export const normalise = (a: Vec3): Vec3 => {
  const l = length(a);
  return l > 1e-12 ? { x: a.x / l, y: a.y / l, z: a.z / l } : { x: 0, y: 0, z: 0 };
};
